import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Play, Download, RefreshCw, AlertCircle,
  AlertTriangle, ChevronDown, ChevronUp, Code, BarChart2, Table
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { csvCell } from '../utils/csvSafe';
import { NavBar } from './NavBar';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell
} from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ColumnDef {
  key:  string;
  label: string;
  type: 'string' | 'number';
}

interface TableDef {
  label:   string;
  columns: ColumnDef[];
}

interface SelectedColumn {
  key:        string;
  aggregate?: string;
}

interface FilterRow {
  id:       string;
  column:   string;
  operator: string;
  value:    string;
}

interface OrderBy {
  column:    string;
  direction: 'ASC' | 'DESC';
}

type ViewMode = 'table' | 'chart';

// ─── Constants ────────────────────────────────────────────────────────────────

const OPERATORS_STRING = ['=', '!=', 'contains', 'starts', 'IS NULL', 'IS NOT NULL'];
const OPERATORS_NUMBER = ['=', '!=', '>', '>=', '<', '<=', 'IS NULL', 'IS NOT NULL'];
const AGGREGATES       = ['', 'SUM', 'COUNT', 'AVG', 'MIN', 'MAX', 'COUNT DISTINCT'];
const ROW_LIMIT_OPTIONS = [
  { value: '100',  label: '100 rows'   },
  { value: '500',  label: '500 rows'   },
  { value: '1000', label: '1,000 rows' },
  { value: '5000', label: '5,000 rows' },
];

const CHART_COLORS = [
  '#2563eb', '#16a34a', '#9333ea', '#ea580c',
  '#0891b2', '#be185d', '#854d0e', '#166534',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

function toNum(s: string | undefined): number {
  if (!s) return 0;
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function exportCSV(data: Record<string, string>[], filename: string) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows    = data.map(r =>
    headers.map(h => csvCell(r[h])).join(',')
  );
  const csv  = [headers.map(csvCell).join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Step indicators ──────────────────────────────────────────────────────────

function StepBadge({ n, active, done }: { n: number; active: boolean; done: boolean }) {
  return (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
      ${done   ? 'bg-green-500 text-white' :
        active ? 'bg-blue-600 text-white'  :
                 'bg-gray-200 text-gray-500'}`}>
      {done ? '✓' : n}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function CustomQuery() {
  const { getToken } = useAuth();
  const API_URL      = process.env.REACT_APP_API_URL ?? '';

  // Catalogue
  const [catalogue,  setCatalogue]  = useState<Record<string, TableDef>>({});
  const [catLoading, setCatLoading] = useState(true);

  // Builder state
  const [table,      setTable]      = useState('');
  const [selCols,    setSelCols]    = useState<SelectedColumn[]>([]);
  const [filters,    setFilters]    = useState<FilterRow[]>([]);
  const [groupBy,    setGroupBy]    = useState<string[]>([]);
  const [orderBy,    setOrderBy]    = useState<OrderBy>({ column: '', direction: 'ASC' });
  const [rowLimit,   setRowLimit]   = useState('1000');

  // Results
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [data,       setData]       = useState<Record<string, string>[]>([]);
  const [truncated,  setTruncated]  = useState(false);
  const [hasRun,     setHasRun]     = useState(false);
  const [sqlPreview, setSqlPreview] = useState('');
  const [showSql,    setShowSql]    = useState(false);
  const [viewMode,   setViewMode]   = useState<ViewMode>('table');

  // Load catalogue on mount
  useEffect(() => {
    const load = async () => {
      try {
        const token = await getToken();
        const res   = await fetch(`${API_URL}/reports/generate`, {
          method:  'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body:    JSON.stringify({ report_id: 'custom.catalogue', params: {} }),
        });
        const result = await res.json();
        setCatalogue(result.catalogue ?? {});
      } catch (e) {
        console.error('Failed to load catalogue:', e);
      } finally {
        setCatLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableDef  = catalogue[table];
  const tableCols = tableDef?.columns ?? [];

  // Reset downstream when table changes
  const handleTableChange = (t: string) => {
    setTable(t);
    setSelCols([]);
    setFilters([]);
    setGroupBy([]);
    setOrderBy({ column: '', direction: 'ASC' });
    setData([]);
    setHasRun(false);
    setError(null);
  };

  // Column selection
  const toggleCol = (key: string) => {
    setSelCols(prev => {
      const exists = prev.find(c => c.key === key);
      if (exists) return prev.filter(c => c.key !== key);
      return [...prev, { key }];
    });
  };

  const setAggregate = (key: string, agg: string) => {
    setSelCols(prev => prev.map(c => c.key === key ? { ...c, aggregate: agg || undefined } : c));
  };

  const selectAll = () => setSelCols(tableCols.map(c => ({ key: c.key })));
  const clearAll  = () => setSelCols([]);

  // Filters
  const addFilter = () => setFilters(prev => [...prev, { id: uid(), column: tableCols[0]?.key ?? '', operator: '=', value: '' }]);
  const removeFilter = (id: string) => setFilters(prev => prev.filter(f => f.id !== id));
  const updateFilter = (id: string, field: keyof FilterRow, val: string) =>
    setFilters(prev => prev.map(f => f.id === id ? { ...f, [field]: val } : f));

  // Group by
  const toggleGroupBy = (key: string) =>
    setGroupBy(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  // Has aggregates?
  const hasAggregates = selCols.some(c => c.aggregate);

  // Validation
  const canRun = table && selCols.length > 0 &&
    filters.every(f => f.operator.includes('NULL') || f.value.trim() !== '');

  // Run query
  const runQuery = async () => {
    if (!canRun) return;
    setLoading(true);
    setError(null);

    try {
      const token  = await getToken();
      const params = {
        table,
        columns:  selCols,
        filters:  filters.map(({ id, ...rest }) => rest),
        groupBy:  hasAggregates ? groupBy : [],
        orderBy,
        row_limit: rowLimit,
      };

      const res = await fetch(`${API_URL}/reports/generate`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ report_id: 'custom.query', params }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error ?? `HTTP ${res.status}`);
      }

      const result = await res.json();
      setData(result.data ?? []);
      setTruncated(result.truncated ?? false);
      setHasRun(true);

      // Build SQL preview from result if available
      if (result.sql) setSqlPreview(result.sql);

    } catch (e: any) {
      setError(e.message ?? 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  // Chart data — use first string col as label, first number col as value
  const chartData = (() => {
    if (!data.length) return [];
    const headers    = Object.keys(data[0]);
    const numericCol = headers.find(h => !isNaN(toNum(data[0][h])) && data[0][h] !== '');
    const labelCol   = headers.find(h => isNaN(toNum(data[0][h])) || data[0][h] === '');
    if (!numericCol || !labelCol) return [];
    return data.slice(0, 20).map(row => ({
      name:  row[labelCol] ?? '',
      value: toNum(row[numericCol]),
    }));
  })();

  const canShowChart = chartData.length > 0;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

        {/* Page header */}
        <div>
          <h1 className="text-xl font-bold text-gray-900">Custom Query Builder</h1>
          <p className="text-sm text-gray-500 mt-0.5">Build a custom query without writing SQL</p>
        </div>

        {catLoading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400">
            <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
            <p className="text-sm">Loading tables…</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* ── Left panel — builder ────────────────────────────────────── */}
            <div className="lg:col-span-1 space-y-4">

              {/* Step 1 — Table */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-center space-x-3 mb-4">
                  <StepBadge n={1} active={!table} done={!!table} />
                  <h2 className="text-sm font-bold text-gray-900">Choose a Table</h2>
                </div>
                <select
                  value={table}
                  onChange={e => handleTableChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a table…</option>
                  {Object.entries(catalogue)
                    .sort(([, a], [, b]) => a.label.localeCompare(b.label))
                    .map(([key, def]) => (
                      <option key={key} value={key}>{def.label}</option>
                    ))}
                </select>
              </div>

              {/* Step 2 — Columns */}
              {table && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <div className="flex items-center space-x-3 mb-3">
                    <StepBadge n={2} active={selCols.length === 0} done={selCols.length > 0} />
                    <h2 className="text-sm font-bold text-gray-900">Select Columns</h2>
                  </div>
                  <div className="flex space-x-2 mb-3">
                    <button onClick={selectAll} className="text-xs text-blue-600 hover:underline">Select all</button>
                    <span className="text-gray-300">|</span>
                    <button onClick={clearAll}  className="text-xs text-gray-500 hover:underline">Clear</button>
                  </div>
                  <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
                    {tableCols.map(col => {
                      const sel = selCols.find(c => c.key === col.key);
                      return (
                        <div key={col.key} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={!!sel}
                            onChange={() => toggleCol(col.key)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-700 flex-1">{col.label}</span>
                          {sel && col.type === 'number' && (
                            <select
                              value={sel.aggregate ?? ''}
                              onChange={e => setAggregate(col.key, e.target.value)}
                              className="text-xs border border-gray-200 rounded px-1 py-0.5 text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            >
                              {AGGREGATES.map(a => (
                                <option key={a} value={a}>{a || 'No agg.'}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {selCols.length > 0 && (
                    <p className="text-xs text-gray-400 mt-2">{selCols.length} column{selCols.length !== 1 ? 's' : ''} selected</p>
                  )}
                </div>
              )}

              {/* Step 3 — Filters */}
              {table && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <StepBadge n={3} active={false} done={filters.length > 0} />
                      <h2 className="text-sm font-bold text-gray-900">Filters <span className="text-gray-400 font-normal">(optional)</span></h2>
                    </div>
                    <button
                      onClick={addFilter}
                      className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-700"
                    >
                      <Plus className="w-3 h-3" /><span>Add</span>
                    </button>
                  </div>

                  {filters.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No filters — all rows will be returned</p>
                  ) : (
                    <div className="space-y-3">
                      {filters.map(f => {
                        const colDef = tableCols.find(c => c.key === f.column);
                        const ops    = colDef?.type === 'number' ? OPERATORS_NUMBER : OPERATORS_STRING;
                        const noVal  = f.operator.includes('NULL');
                        return (
                          <div key={f.id} className="space-y-1.5">
                            <div className="flex items-center space-x-1.5">
                              <select
                                value={f.column}
                                onChange={e => updateFilter(f.id, 'column', e.target.value)}
                                className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                              >
                                {tableCols.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                              </select>
                              <button onClick={() => removeFilter(f.id)} className="text-gray-400 hover:text-red-500">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <div className="flex items-center space-x-1.5">
                              <select
                                value={f.operator}
                                onChange={e => updateFilter(f.id, 'operator', e.target.value)}
                                className="w-32 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                              >
                                {ops.map(op => <option key={op} value={op}>{op}</option>)}
                              </select>
                              {!noVal && (
                                <input
                                  type="text"
                                  value={f.value}
                                  onChange={e => updateFilter(f.id, 'value', e.target.value)}
                                  placeholder="Value"
                                  className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Step 4 — Group By */}
              {table && hasAggregates && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <div className="flex items-center space-x-3 mb-3">
                    <StepBadge n={4} active={false} done={groupBy.length > 0} />
                    <h2 className="text-sm font-bold text-gray-900">Group By</h2>
                  </div>
                  <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                    {selCols.filter(c => !c.aggregate).map(sc => {
                      const colDef = tableCols.find(c => c.key === sc.key);
                      return (
                        <div key={sc.key} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={groupBy.includes(sc.key)}
                            onChange={() => toggleGroupBy(sc.key)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-700">{colDef?.label ?? sc.key}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 5 — Sort + Row Limit */}
              {table && selCols.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
                  <div className="flex items-center space-x-3 mb-1">
                    <StepBadge n={hasAggregates ? 5 : 4} active={false} done={false} />
                    <h2 className="text-sm font-bold text-gray-900">Sort &amp; Limit</h2>
                  </div>

                  {/* Sort */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Sort by</label>
                      <select
                        value={orderBy.column}
                        onChange={e => setOrderBy(o => ({ ...o, column: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">None</option>
                        {selCols.map(sc => {
                          const colDef = tableCols.find(c => c.key === sc.key);
                          const label  = sc.aggregate
                            ? `${sc.aggregate}(${colDef?.label ?? sc.key})`
                            : colDef?.label ?? sc.key;
                          return <option key={sc.key} value={sc.key}>{label}</option>;
                        })}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Direction</label>
                      <select
                        value={orderBy.direction}
                        onChange={e => setOrderBy(o => ({ ...o, direction: e.target.value as 'ASC' | 'DESC' }))}
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="ASC">Ascending</option>
                        <option value="DESC">Descending</option>
                      </select>
                    </div>
                  </div>

                  {/* Row limit */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Max rows</label>
                    <select
                      value={rowLimit}
                      onChange={e => setRowLimit(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {ROW_LIMIT_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  {Number(rowLimit) > 1000 && (
                    <div className="flex items-start space-x-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-yellow-700">Requesting more than 1,000 rows may be slow.</p>
                    </div>
                  )}

                  {/* Run button */}
                  <button
                    onClick={runQuery}
                    disabled={loading || !canRun}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    <span>{loading ? 'Running…' : 'Run Query'}</span>
                  </button>
                </div>
              )}
            </div>

            {/* ── Right panel — results ───────────────────────────────────── */}
            <div className="lg:col-span-2 space-y-4">

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-red-800">Query failed</p>
                    <p className="text-sm text-red-600 mt-0.5">{error}</p>
                  </div>
                </div>
              )}

              {/* Truncation warning */}
              {truncated && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                  <p className="text-xs text-yellow-700">
                    Results limited to {Number(rowLimit).toLocaleString()} rows. Add filters or increase the limit to see more.
                  </p>
                </div>
              )}

              {/* Results toolbar */}
              {hasRun && data.length > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${viewMode === 'table' ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                    >
                      <Table className="w-3.5 h-3.5" /><span>Table</span>
                    </button>
                    {canShowChart && (
                      <button
                        onClick={() => setViewMode('chart')}
                        className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${viewMode === 'chart' ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                      >
                        <BarChart2 className="w-3.5 h-3.5" /><span>Chart</span>
                      </button>
                    )}
                    <button
                      onClick={() => setShowSql(x => !x)}
                      className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${showSql ? 'bg-gray-100 text-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                    >
                      <Code className="w-3.5 h-3.5" /><span>SQL</span>
                    </button>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-xs text-gray-400">{data.length.toLocaleString()} row{data.length !== 1 ? 's' : ''}</span>
                    <button
                      onClick={() => exportCSV(data, `custom-query-${table}.csv`)}
                      className="flex items-center space-x-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" /><span>Export CSV</span>
                    </button>
                  </div>
                </div>
              )}

              {/* SQL preview */}
              {showSql && sqlPreview && (
                <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
                  <pre className="text-xs text-green-400 whitespace-pre-wrap">{sqlPreview}</pre>
                </div>
              )}

              {/* Chart view */}
              {hasRun && data.length > 0 && viewMode === 'chart' && canShowChart && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11 }}
                        angle={-35}
                        textAnchor="end"
                        interval={0}
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: number) => v.toLocaleString()} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {chartData.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Table view */}
              {hasRun && viewMode === 'table' && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  {data.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                      <Table className="w-12 h-12 mx-auto mb-3 opacity-25" />
                      <p className="text-sm">No data found for the selected filters.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            {Object.keys(data[0]).map(h => (
                              <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                                {h.replace(/_/g, ' ')}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {data.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-50 transition-colors">
                              {Object.keys(data[0]).map(h => (
                                <td key={h} className="px-3 py-2 text-gray-700 whitespace-nowrap text-xs">
                                  {row[h] ?? '—'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Empty state */}
              {!hasRun && !loading && !error && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-24">
                  <BarChart2 className="w-16 h-16 mx-auto mb-4 text-gray-200" />
                  <p className="text-base font-medium text-gray-500">Select a table and columns to get started</p>
                  <p className="text-sm text-gray-400 mt-1">Results will appear here after running your query</p>
                </div>
              )}

            </div>
          </div>
        )}
      </main>
    </div>
  );
}
